// Localização: ./src/pages/Management/VincularPacientesPage.jsx
// Descrição: Página para gerenciar vínculos entre usuários e pacientes

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/api';
import PageTitle from '../../components/Layout/PageTitle';
import LoadingIndicator from '../../components/Common/LoadingIndicator';
import { 
    Link2, Search, UserPlus, Users, AlertCircle, CheckCircle2, X, 
    User, UserX, Eye, Trash2, Plus, Filter
} from 'lucide-react';

const VincularPacientesPage = () => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    
    // Estados para dados
    const [usuarios, setUsuarios] = useState([]);
    const [pacientes, setPacientes] = useState([]);
    const [vinculos, setVinculos] = useState([]);
    
    // Estados para filtros e busca
    const [buscaUsuario, setBuscaUsuario] = useState('');
    const [buscaPaciente, setBuscaPaciente] = useState('');
    const [filtroTipo, setFiltroTipo] = useState('todos'); // 'todos', 'por-usuario', 'por-paciente'
    
    // Estados para modais
    const [modalVincular, setModalVincular] = useState(false);
    const [modalDetalhes, setModalDetalhes] = useState(false);
    const [detalhesVinculo, setDetalhesVinculo] = useState(null);
    const [usuarioSelecionado, setUsuarioSelecionado] = useState(null);
    const [pacientesSelecionados, setPacientesSelecionados] = useState([]);
    const [vinculando, setVinculando] = useState(false);

    // Carregar dados iniciais
    useEffect(() => {
        carregarDados();
    }, []);

    const carregarDados = async () => {
        setLoading(true);
        setError(null);
        
        try {
            // Carregar usuários, pacientes e vínculos em paralelo
            const [usuariosRes, pacientesRes, vinculosRes] = await Promise.all([
                api.get('/usuarios'),
                api.get('/pacientes'),
                api.get('/vinculos/todos')
            ]);
            
            setUsuarios(usuariosRes.data || []);
            setPacientes(pacientesRes.data || []);
            setVinculos(vinculosRes.data?.vinculos || []);
        } catch (err) {
            console.error('Erro ao carregar dados:', err);
            setError(err.response?.data?.error || 'Erro ao carregar dados. Tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    // Filtrar usuários
    const usuariosFiltrados = usuarios.filter(u => {
        if (!buscaUsuario) return true;
        const busca = buscaUsuario.toLowerCase();
        return (
            u.nome_completo?.toLowerCase().includes(busca) ||
            u.email?.toLowerCase().includes(busca) ||
            u.role?.toLowerCase().includes(busca)
        );
    });

    // Filtrar pacientes
    const pacientesFiltrados = pacientes.filter(p => {
        if (!buscaPaciente) return true;
        const busca = buscaPaciente.toLowerCase();
        return p.nome_completo?.toLowerCase().includes(busca);
    });

    // Filtrar vínculos
    const vinculosFiltrados = vinculos.filter(v => {
        if (filtroTipo === 'todos') return true;
        if (filtroTipo === 'por-usuario' && !buscaUsuario) return true;
        if (filtroTipo === 'por-paciente' && !buscaPaciente) return true;
        
        if (filtroTipo === 'por-usuario') {
            const busca = buscaUsuario.toLowerCase();
            return (
                v.usuario_nome?.toLowerCase().includes(busca) ||
                v.usuario_email?.toLowerCase().includes(busca)
            );
        }
        
        if (filtroTipo === 'por-paciente') {
            const busca = buscaPaciente.toLowerCase();
            return v.paciente_nome?.toLowerCase().includes(busca);
        }
        
        return true;
    });

    // Abrir modal de vincular
    const abrirModalVincular = (usuario) => {
        setUsuarioSelecionado(usuario);
        setPacientesSelecionados([]);
        setModalVincular(true);
    };

    // Toggle seleção de paciente
    const togglePacienteSelecionado = (pacienteId) => {
        setPacientesSelecionados(prev => {
            if (prev.includes(pacienteId)) {
                return prev.filter(id => id !== pacienteId);
            }
            return [...prev, pacienteId];
        });
    };

    // Vincular pacientes
    const handleVincular = async () => {
        if (!usuarioSelecionado || pacientesSelecionados.length === 0) {
            setError('Selecione pelo menos um paciente para vincular.');
            return;
        }

        setVinculando(true);
        setError(null);

        try {
            if (pacientesSelecionados.length === 1) {
                // Vincular um único paciente
                await api.post('/vinculos/vincular', {
                    pacienteId: pacientesSelecionados[0],
                    userId: usuarioSelecionado.id
                });
            } else {
                // Vincular múltiplos pacientes
                await api.post('/vinculos/vincular-multiplos', {
                    pacienteIds: pacientesSelecionados,
                    userId: usuarioSelecionado.id
                });
            }

            setSuccess(`Paciente(s) vinculado(s) com sucesso!`);
            setModalVincular(false);
            setUsuarioSelecionado(null);
            setPacientesSelecionados([]);
            carregarDados();
        } catch (err) {
            console.error('Erro ao vincular:', err);
            setError(err.response?.data?.error || 'Erro ao vincular paciente(s). Tente novamente.');
        } finally {
            setVinculando(false);
        }
    };

    // Desvincular
    const handleDesvincular = async (vinculo) => {
        if (!window.confirm(`Deseja realmente desvincular ${vinculo.paciente_nome} de ${vinculo.usuario_nome}?`)) {
            return;
        }

        setLoading(true);
        setError(null);

        try {
            await api.post('/vinculos/desvincular', {
                pacienteId: vinculo.paciente_id,
                userId: vinculo.usuario_id
            });

            setSuccess('Vínculo removido com sucesso!');
            carregarDados();
        } catch (err) {
            console.error('Erro ao desvincular:', err);
            setError(err.response?.data?.error || 'Erro ao desvincular. Tente novamente.');
            setLoading(false);
        }
    };

    // Ver detalhes de vínculos de um usuário
    const verPacientesDoUsuario = async (usuarioId) => {
        setLoading(true);
        setError(null);

        try {
            const response = await api.get(`/vinculos/usuario/${usuarioId}/pacientes`);
            setDetalhesVinculo({
                tipo: 'usuario',
                dados: response.data
            });
            setModalDetalhes(true);
        } catch (err) {
            console.error('Erro ao buscar pacientes do usuário:', err);
            setError(err.response?.data?.error || 'Erro ao buscar pacientes vinculados.');
        } finally {
            setLoading(false);
        }
    };

    // Ver detalhes de vínculos de um paciente
    const verUsuariosDoPaciente = async (pacienteId) => {
        setLoading(true);
        setError(null);

        try {
            const response = await api.get(`/vinculos/paciente/${pacienteId}/usuarios`);
            setDetalhesVinculo({
                tipo: 'paciente',
                dados: response.data
            });
            setModalDetalhes(true);
        } catch (err) {
            console.error('Erro ao buscar usuários do paciente:', err);
            setError(err.response?.data?.error || 'Erro ao buscar usuários vinculados.');
        } finally {
            setLoading(false);
        }
    };

    // Obter role label
    const getRoleLabel = (role) => {
        const labels = {
            'proprietario': 'Proprietário',
            'admin': 'Administrador',
            'gerente': 'Gerente',
            'enfermeiro': 'Enfermeiro',
            'medico': 'Médico',
            'fisioterapeuta': 'Fisioterapeuta',
            'cuidador': 'Cuidador',
        };
        return labels[role] || role;
    };

    if (loading && usuarios.length === 0) {
        return <LoadingIndicator message="Carregando dados..." />;
    }

    return (
        <div className="p-4 sm:p-6 lg:p-8 min-h-screen">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <PageTitle 
                    title="Gerenciar Vínculos"
                    subtitle="Vincule pacientes a usuários para controlar o acesso aos dados"
                />

                {/* Mensagens de Erro/Sucesso */}
                {error && (
                    <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-500 rounded flex items-center">
                        <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
                        <span className="text-red-700">{error}</span>
                        <button 
                            onClick={() => setError(null)}
                            className="ml-auto text-red-500 hover:text-red-700"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                )}

                {success && (
                    <div className="mb-4 p-4 bg-green-50 border-l-4 border-green-500 rounded flex items-center">
                        <CheckCircle2 className="w-5 h-5 text-green-500 mr-2" />
                        <span className="text-green-700">{success}</span>
                        <button 
                            onClick={() => setSuccess(null)}
                            className="ml-auto text-green-500 hover:text-green-700"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                )}

                {/* Filtros */}
                <div className="mb-6 bg-white shadow-lg rounded-xl p-4">
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Buscar Usuário
                            </label>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                <input
                                    type="text"
                                    value={buscaUsuario}
                                    onChange={(e) => setBuscaUsuario(e.target.value)}
                                    placeholder="Nome, email ou role..."
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                                />
                            </div>
                        </div>
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Buscar Paciente
                            </label>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                <input
                                    type="text"
                                    value={buscaPaciente}
                                    onChange={(e) => setBuscaPaciente(e.target.value)}
                                    placeholder="Nome do paciente..."
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                                />
                            </div>
                        </div>
                        <div className="flex items-end">
                            <select
                                value={filtroTipo}
                                onChange={(e) => setFiltroTipo(e.target.value)}
                                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                            >
                                <option value="todos">Todos os Vínculos</option>
                                <option value="por-usuario">Por Usuário</option>
                                <option value="por-paciente">Por Paciente</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Lista de Vínculos */}
                <div className="bg-white shadow-lg rounded-xl overflow-hidden">
                    <div className="p-4 border-b border-gray-200">
                        <h2 className="text-xl font-bold text-gray-900 flex items-center">
                            <Link2 className="w-5 h-5 mr-2 text-emerald-600" />
                            Vínculos ({vinculosFiltrados.length})
                        </h2>
                    </div>
                    
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Usuário
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Paciente
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Data do Vínculo
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Ações
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {vinculosFiltrados.length === 0 ? (
                                    <tr>
                                        <td colSpan="4" className="px-4 py-8 text-center text-gray-500">
                                            Nenhum vínculo encontrado.
                                        </td>
                                    </tr>
                                ) : (
                                    vinculosFiltrados.map((vinculo) => (
                                        <tr key={vinculo.vinculo_id} className="hover:bg-gray-50">
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <div>
                                                        <div className="text-sm font-medium text-gray-900">
                                                            {vinculo.usuario_nome}
                                                        </div>
                                                        <div className="text-sm text-gray-500">
                                                            {getRoleLabel(vinculo.usuario_role)}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <div className="text-sm font-medium text-gray-900">
                                                    {vinculo.paciente_nome}
                                                </div>
                                                <div className="text-sm text-gray-500">
                                                    Status: {vinculo.paciente_status}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                                {new Date(vinculo.data_vinculo).toLocaleDateString('pt-BR')}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        onClick={() => verPacientesDoUsuario(vinculo.usuario_id)}
                                                        className="text-emerald-600 hover:text-emerald-900"
                                                        title="Ver pacientes do usuário"
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => verUsuariosDoPaciente(vinculo.paciente_id)}
                                                        className="text-blue-600 hover:text-blue-900"
                                                        title="Ver usuários do paciente"
                                                    >
                                                        <Users className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDesvincular(vinculo)}
                                                        className="text-red-600 hover:text-red-900"
                                                        title="Desvincular"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Lista de Usuários para Vincular */}
                <div className="mt-6 bg-white shadow-lg rounded-xl overflow-hidden">
                    <div className="p-4 border-b border-gray-200">
                        <h2 className="text-xl font-bold text-gray-900 flex items-center">
                            <Users className="w-5 h-5 mr-2 text-emerald-600" />
                            Usuários ({usuariosFiltrados.length})
                        </h2>
                    </div>
                    
                    <div className="p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {usuariosFiltrados.map((usuario) => {
                                const pacientesVinculados = vinculos.filter(
                                    v => v.usuario_id === usuario.id
                                ).length;

                                return (
                                    <div
                                        key={usuario.id}
                                        className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                                    >
                                        <div className="flex items-center justify-between mb-2">
                                            <h3 className="font-semibold text-gray-900">
                                                {usuario.nome_completo}
                                            </h3>
                                            <span className="text-xs bg-emerald-100 text-emerald-800 px-2 py-1 rounded">
                                                {getRoleLabel(usuario.role)}
                                            </span>
                                        </div>
                                        <p className="text-sm text-gray-500 mb-3">
                                            {pacientesVinculados} paciente(s) vinculado(s)
                                        </p>
                                        <button
                                            onClick={() => abrirModalVincular(usuario)}
                                            className="w-full bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2"
                                        >
                                            <UserPlus className="w-4 h-4" />
                                            Vincular Pacientes
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Modal de Vincular */}
                {modalVincular && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                            <div className="p-6 border-b border-gray-200">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-xl font-bold text-gray-900">
                                        Vincular Pacientes a {usuarioSelecionado?.nome_completo}
                                    </h3>
                                    <button
                                        onClick={() => {
                                            setModalVincular(false);
                                            setUsuarioSelecionado(null);
                                            setPacientesSelecionados([]);
                                        }}
                                        className="text-gray-400 hover:text-gray-600"
                                    >
                                        <X className="w-6 h-6" />
                                    </button>
                                </div>
                            </div>
                            
                            <div className="p-6">
                                <div className="mb-4">
                                    <input
                                        type="text"
                                        placeholder="Buscar paciente..."
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                                        onChange={(e) => {
                                            const busca = e.target.value.toLowerCase();
                                            // Filtrar pacientes na lista
                                        }}
                                    />
                                </div>
                                
                                <div className="space-y-2 max-h-96 overflow-y-auto">
                                    {pacientesFiltrados.map((paciente) => {
                                        const jaVinculado = vinculos.some(
                                            v => v.paciente_id === paciente.id && v.usuario_id === usuarioSelecionado?.id
                                        );
                                        
                                        return (
                                            <label
                                                key={paciente.id}
                                                className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                                                    pacientesSelecionados.includes(paciente.id)
                                                        ? 'bg-emerald-50 border-emerald-500'
                                                        : 'border-gray-200 hover:border-emerald-300'
                                                } ${jaVinculado ? 'opacity-50' : ''}`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={pacientesSelecionados.includes(paciente.id)}
                                                    onChange={() => !jaVinculado && togglePacienteSelecionado(paciente.id)}
                                                    disabled={jaVinculado}
                                                    className="mr-3"
                                                />
                                                <div className="flex-1">
                                                    <div className="font-medium text-gray-900">
                                                        {paciente.nome_completo}
                                                    </div>
                                                    {jaVinculado && (
                                                        <div className="text-xs text-gray-500">
                                                            Já vinculado
                                                        </div>
                                                    )}
                                                </div>
                                            </label>
                                        );
                                    })}
                                </div>
                                
                                <div className="mt-4 flex justify-end gap-2">
                                    <button
                                        onClick={() => {
                                            setModalVincular(false);
                                            setUsuarioSelecionado(null);
                                            setPacientesSelecionados([]);
                                        }}
                                        className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        onClick={handleVincular}
                                        disabled={vinculando || pacientesSelecionados.length === 0}
                                        className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {vinculando ? 'Vinculando...' : `Vincular (${pacientesSelecionados.length})`}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal de Detalhes */}
                {modalDetalhes && detalhesVinculo && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                            <div className="p-6 border-b border-gray-200">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-xl font-bold text-gray-900">
                                        {detalhesVinculo.tipo === 'usuario' 
                                            ? `Pacientes de ${detalhesVinculo.dados.usuario.nome_completo}`
                                            : `Usuários de ${detalhesVinculo.dados.paciente.nome_completo}`
                                        }
                                    </h3>
                                    <button
                                        onClick={() => {
                                            setModalDetalhes(false);
                                            setDetalhesVinculo(null);
                                        }}
                                        className="text-gray-400 hover:text-gray-600"
                                    >
                                        <X className="w-6 h-6" />
                                    </button>
                                </div>
                            </div>
                            
                            <div className="p-6">
                                {detalhesVinculo.dados.pacientes || detalhesVinculo.dados.usuarios ? (
                                    <div className="space-y-2">
                                        {(detalhesVinculo.dados.pacientes || detalhesVinculo.dados.usuarios).map((item) => (
                                            <div
                                                key={item.id}
                                                className="p-3 border border-gray-200 rounded-lg"
                                            >
                                                <div className="font-medium text-gray-900">
                                                    {item.nome_completo}
                                                </div>
                                                {item.role && (
                                                    <div className="text-sm text-gray-500">
                                                        {getRoleLabel(item.role)}
                                                    </div>
                                                )}
                                                {item.status && (
                                                    <div className="text-sm text-gray-500">
                                                        Status: {item.status}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-gray-500 text-center py-4">
                                        Nenhum vínculo encontrado.
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default VincularPacientesPage;

